import i18next from "i18next";
import { NotificationManager } from "react-notifications";

import ObjectExistsStatus from "enums/objectExistsStatus";
import ICustomer from "interfaces/ICustomer";
import CustomerProvider from "providers/customerProvider";

import LoaderTypes from "../../enums/loaderTypes";
import TranslationMapper from "../../i18n/mapper";
import ICustomerVenue from "../../interfaces/ICustomerVenue";
import CleaningManagementService from "../../services/cleaningManagementService";
import { ActionTypes } from "../actionTypes";
import { actionCreator, AppAction } from "../appAction";

import { fetchedCustomerActivities, fetchedCustomerActivityTypes, getCustomerActivityTypes } from "./activityActions";
import { getCustomerIncidentTypes } from "./incidentTypeActions";
import { setAsLoading, unsetAsLoading } from "./loaderActions";
import { fetchedCustomerLocationOverview, getCustomerLocationOverview, getCustomerLocations } from "./locationActions";
import { getOperatorsAsync } from "./operatorActions";

export const fetchedCustomers = actionCreator<ActionTypes.FETCHED_CUSTOMERS, ICustomer[]>(
  ActionTypes.FETCHED_CUSTOMERS,
);

export const updateSelectedCustomer = actionCreator<ActionTypes.UPDATE_SELECTED_CUSTOMER, string>(
  ActionTypes.UPDATE_SELECTED_CUSTOMER,
);
export const displayCustomerSelection = actionCreator<ActionTypes.SHOW_CUSTOMER_SELECTION, boolean>(
  ActionTypes.SHOW_CUSTOMER_SELECTION,
);
export const updateHasLogbook = actionCreator<ActionTypes.UPDATE_HAS_LOGBOOK, boolean>(ActionTypes.UPDATE_HAS_LOGBOOK);
export const updateHasOperation = actionCreator<ActionTypes.UPDATE_HAS_OPERATION, boolean>(
  ActionTypes.UPDATE_HAS_OPERATION,
);

export function getCustomers(): AppAction {
  return (dispatch): void => {
    dispatch(setAsLoading(LoaderTypes.Customers));
    const cleaningManagementService = new CleaningManagementService();

    cleaningManagementService
      .getCustomerOverview()
      .then(customers => {
        dispatch(fetchedCustomers(customers));
        dispatch(updateHasLogbook(customers.some(c => c.hasLogbook)));
        dispatch(updateHasOperation(customers.some(c => c.hasOperation)));
      })
      .finally(() => {
        dispatch(unsetAsLoading(LoaderTypes.Customers));
      });
  };
}

export function upsertCustomer(customer: ICustomer): AppAction {
  return (dispatch): void => {
    const cleaningManagementService = new CleaningManagementService();

    dispatch(setAsLoading(LoaderTypes.Customers));

    cleaningManagementService
      .upsertCustomer(customer)
      .then(() => {
        NotificationManager.success(i18next.t(TranslationMapper.pages.customeredit.customer_updated_message));

        dispatch(getCustomers());
      })
      .catch(() => {
        NotificationManager.error(i18next.t(TranslationMapper.pages.customeredit.error_upserting_customer));
        dispatch(unsetAsLoading(LoaderTypes.Customers));
      });
  };
}

export function updateActiveCustomer(activeCustomerId: string): AppAction {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  return (dispatch, getState) => {
    CustomerProvider.clear();
    dispatch(clearCustomerState());

    if (activeCustomerId !== "") {
      const activeCustomer = getState().customerState.customers.find(c => c.id === activeCustomerId);
      if (activeCustomer) {
        CustomerProvider.saveActiveCustomer(activeCustomer);
      }

      dispatch(getCustomerActivityTypes(activeCustomerId));
      dispatch(getCustomerLocations(activeCustomerId));
      dispatch(getOperatorsAsync(activeCustomerId));
      dispatch(getCustomerIncidentTypes(activeCustomerId));
      dispatch(updateSelectedCustomer(activeCustomerId));

      // clear customer specific state
      dispatch(
        fetchedCustomerActivities({
          activityActionResponse: [],
          totalResultsCount: 0,
          showMoreResultsMessage: false,
        }),
      );
    }
  };
}

export function getVenueExists(
  customerId: string,
  venue: ICustomerVenue,
  onVenueExists: (status: ObjectExistsStatus) => void,
  onVenueIsNew: () => void,
): AppAction {
  return (): void => {
    const cleaningManagementService = new CleaningManagementService();

    cleaningManagementService
      .checkCustomerVenueExists(customerId, venue.externalVenueId)
      .then((status: ObjectExistsStatus) => {
        if (status === ObjectExistsStatus.IsActive || status === ObjectExistsStatus.IsDeleted) {
          onVenueExists(status);
        } else {
          onVenueIsNew();
        }
      });
  };
}

export function updateVenue(customerId: string, venue: ICustomerVenue, OnUpsertVenue?: () => void): AppAction {
  return (dispatch): void => {
    const cleaningManagementService = new CleaningManagementService();

    dispatch(setAsLoading(LoaderTypes.Locations));

    cleaningManagementService
      .upsertCustomerVenue(customerId, venue)
      .then(() => {
        NotificationManager.success(i18next.t(TranslationMapper.pages.masterdata.savedchanges));
        dispatch(getCustomers());
        dispatch(getCustomerLocationOverview(customerId));
        if (OnUpsertVenue != null) {
          OnUpsertVenue();
        }
      })
      .catch(() => {
        NotificationManager.error(i18next.t(TranslationMapper.pages.customeredit.errormessage_updatelocation));
      })
      .finally(() => {
        dispatch(unsetAsLoading(LoaderTypes.Locations));
      });
  };
}

export function deleteVenue(customerId: string, venueId: string): AppAction {
  return (dispatch): void => {
    const cleaningManagementService = new CleaningManagementService();

    dispatch(setAsLoading(LoaderTypes.Locations));

    cleaningManagementService
      .deleteCustomerVenue(customerId, venueId)
      .then(() => {
        NotificationManager.success(i18next.t(TranslationMapper.pages.masterdata.savedchanges));
        dispatch(getCustomers());
        dispatch(getCustomerLocationOverview(customerId));
      })
      .catch(() => {
        NotificationManager.error(i18next.t(TranslationMapper.pages.customeredit.errormessage_deletelocation));
      })
      .finally(() => {
        dispatch(unsetAsLoading(LoaderTypes.Locations));
      });
  };
}

export function showCustomerSelection(show: boolean): AppAction {
  return (dispatch): void => {
    dispatch(displayCustomerSelection(show));
  };
}

function clearCustomerState(): AppAction {
  return (dispatch): void => {
    dispatch(
      fetchedCustomerActivities({
        activityActionResponse: [],
        totalResultsCount: 0,
        showMoreResultsMessage: false,
      }),
    );
    dispatch(fetchedCustomerActivityTypes([]));
    dispatch(fetchedCustomerLocationOverview([]));
  };
}
